/**
 * 性能测试套件
 * 全面测试 Gongfu 各模块的性能表现
 */

import { PerformanceMonitor } from '../../core/performance-monitor';
import { TaskStore } from '../../core/task-store';
import { WorkflowManager } from '../../core/workflow-manager';
import { TemplateManager } from '../../core/template-system';
import { AgentManager } from '../../core/agent-manager';
import { PluginManager } from '../../core/plugin-system';
import { logger } from '../../utils/logger';
import * as fs from 'fs-extra';
import * as path from 'path';

/**
 * 测试场景
 */
export interface TestScenario {
  name: string;
  description: string;
  run: () => Promise<void>;
  iterations?: number;
  warmup?: boolean;
}

/**
 * 性能测试套件
 */
export class PerformanceTestSuite {
  private monitor: PerformanceMonitor;
  private testDir: string;
  private scenarios: TestScenario[] = [];
  
  constructor() {
    this.monitor = new PerformanceMonitor();
    this.testDir = path.join(process.cwd(), '.performance-test');
  }
  
  /**
   * 初始化测试环境
   */
  async setup(): Promise<void> {
    await fs.ensureDir(this.testDir);
    await fs.emptyDir(this.testDir);
    
    // 设置性能阈值
    this.monitor.setThresholds({
      slowOperation: 500,     // 500ms
      highMemoryUsage: 50,    // 50MB
      highCpuUsage: 70        // 70%
    });
    
    // 注册测试场景
    this.registerScenarios();
  }
  
  /**
   * 清理测试环境
   */
  async teardown(): Promise<void> {
    await fs.remove(this.testDir);
  }
  
  /**
   * 注册测试场景
   */
  private registerScenarios(): void {
    // 任务管理性能测试
    this.scenarios.push({
      name: 'Task Creation Performance',
      description: '测试创建大量任务的性能',
      run: async () => {
        const manager = new TaskStore(this.testDir);
        await manager.initialize();
        
        const taskCount = 1000;
        for (let i = 0; i < taskCount; i++) {
          await this.monitor.measure(
            'task:create',
            () => manager.createTask({
              content: `Performance test task ${i}`,
              priority: 'medium',
              tags: ['performance', 'test']
            }),
            { index: i }
          );
        }
      },
      iterations: 3
    });
    
    // 任务查询性能测试
    this.scenarios.push({
      name: 'Task Query Performance',
      description: '测试查询和过滤任务的性能',
      run: async () => {
        const manager = new TaskStore(this.testDir);
        await manager.initialize();
        
        // 预先创建任务
        const taskCount = 500;
        for (let i = 0; i < taskCount; i++) {
          await manager.createTask({
            content: `Query test task ${i}`,
            priority: ['high', 'medium', 'low'][i % 3] as any,
            tags: [`tag${i % 10}`, 'test'],
            status: ['todo', 'in_progress', 'done'][i % 3] as any
          });
        }
        
        // 测试不同查询
        await this.monitor.measure('task:list:all', () => manager.listTasks());
        await this.monitor.measure('task:list:filtered', () => manager.listTasks({
          status: 'in_progress'
        }));
        await this.monitor.measure('task:search', () => manager.searchTasks('test'));
        await this.monitor.measure('task:getByTag', () => manager.getTasksByTag('tag5'));
      }
    });
    
    // 模板应用性能测试
    this.scenarios.push({
      name: 'Template Application Performance',
      description: '测试应用工作流模板的性能',
      run: async () => {
        const templateManager = new TemplateManager(this.testDir);
        await templateManager.initialize();
        
        // 创建测试模板
        const template = {
          metadata: {
            id: 'perf-test',
            name: '性能测试模板',
            description: '用于性能测试',
            version: '1.0.0',
            category: 'testing' as any,
            verified: true
          },
          workflow: {
            name: '性能测试工作流',
            description: '测试模板应用性能',
            phases: Array.from({ length: 10 }, (_, i) => ({
              id: `phase-${i}`,
              name: `阶段 ${i}`,
              description: `测试阶段 ${i}`,
              tasks: Array.from({ length: 10 }, (_, j) => ({
                id: `task-${i}-${j}`,
                content: `任务 {{projectName}} - ${i}-${j}`,
                priority: 'medium',
                tags: ['test']
              }))
            }))
          }
        };
        
        await templateManager.createTemplate(template.metadata, template.workflow);
        
        // 测试模板应用
        await this.monitor.measure('template:apply', () =>
          templateManager.applyTemplate('perf-test', {
            projectName: 'perf-test-project',
            projectPath: path.join(this.testDir, 'test-project'),
            variables: {}
          })
        );
      }
    });
    
    // Agent 执行性能测试
    this.scenarios.push({
      name: 'Agent Execution Performance',
      description: '测试 Agent 执行任务的性能',
      run: async () => {
        const agentManager = new AgentManager(this.testDir);
        await agentManager.initialize();
        
        // 注册测试 Agent
        await agentManager.registerAgent({
          id: 'perf-test-agent',
          name: '性能测试 Agent',
          description: '用于性能测试',
          capabilities: ['test'],
          systemPrompt: 'You are a performance test agent.',
          examples: []
        });
        
        // 测试 Agent 选择
        const tasks = Array.from({ length: 100 }, (_, i) => ({
          content: `Test task ${i}`,
          type: 'test'
        }));
        
        for (const task of tasks) {
          await this.monitor.measure('agent:select', () =>
            agentManager.selectAgent(task)
          );
        }
      }
    });
    
    // 插件加载性能测试
    this.scenarios.push({
      name: 'Plugin Loading Performance',
      description: '测试插件加载和执行的性能',
      run: async () => {
        const pluginManager = new PluginManager(this.testDir);
        await pluginManager.initialize();
        
        // 创建测试插件
        const pluginCount = 20;
        for (let i = 0; i < pluginCount; i++) {
          const pluginDir = path.join(this.testDir, 'plugins', `test-plugin-${i}`);
          await fs.ensureDir(pluginDir);
          
          await fs.writeJson(path.join(pluginDir, 'plugin.json'), {
            id: `test-plugin-${i}`,
            name: `Test Plugin ${i}`,
            version: '1.0.0',
            description: 'Performance test plugin',
            main: './index.js',
            permissions: ['tasks:read']
          });
          
          await fs.writeFile(path.join(pluginDir, 'index.js'), `
            module.exports = {
              activate: async (api) => {
                // Simulate plugin work
                await new Promise(resolve => setTimeout(resolve, 10));
              }
            };
          `);
        }
        
        // 测试插件加载
        await this.monitor.measure('plugin:loadAll', () =>
          pluginManager.loadPlugins()
        );
        
        // 测试插件启用
        for (let i = 0; i < pluginCount; i++) {
          await this.monitor.measure('plugin:enable', () =>
            pluginManager.enablePlugin(`test-plugin-${i}`),
            { pluginIndex: i }
          );
        }
      }
    });
    
    // 并发操作性能测试
    this.scenarios.push({
      name: 'Concurrent Operations Performance',
      description: '测试并发操作的性能',
      run: async () => {
        const manager = new TaskStore(this.testDir);
        await manager.initialize();
        
        // 并发创建任务
        const concurrentCount = 50;
        await this.monitor.measure('concurrent:create', async () => {
          await Promise.all(
            Array.from({ length: concurrentCount }, (_, i) =>
              manager.createTask({
                content: `Concurrent task ${i}`,
                priority: 'medium'
              })
            )
          );
        });
        
        // 并发更新任务
        const tasks = await manager.listTasks();
        await this.monitor.measure('concurrent:update', async () => {
          await Promise.all(
            tasks.slice(0, concurrentCount).map(task =>
              manager.updateTask(task.id, { status: 'in_progress' })
            )
          );
        });
      }
    });
    
    // 大文件处理性能测试
    this.scenarios.push({
      name: 'Large File Performance',
      description: '测试处理大文件的性能',
      run: async () => {
        // 创建大的任务文件
        const largeTasks = Array.from({ length: 10000 }, (_, i) => ({
          id: `task-${i}`,
          content: `Large file test task ${i}`,
          status: 'todo',
          priority: 'medium',
          tags: [`tag${i % 100}`],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }));
        
        const filePath = path.join(this.testDir, 'large-tasks.json');
        
        // 测试写入性能
        await this.monitor.measure('file:write:large', () =>
          fs.writeJson(filePath, largeTasks)
        );
        
        // 测试读取性能
        await this.monitor.measure('file:read:large', () =>
          fs.readJson(filePath)
        );
        
        // 测试解析性能
        const content = await fs.readFile(filePath, 'utf-8');
        await this.monitor.measure('json:parse:large', () =>
          JSON.parse(content)
        );
      }
    });
  }
  
  /**
   * 运行所有测试
   */
  async runAll(): Promise<void> {
    logger.info('Starting performance test suite...');
    
    await this.setup();
    
    try {
      for (const scenario of this.scenarios) {
        await this.runScenario(scenario);
      }
      
      // 生成报告
      const report = this.monitor.generateReport();
      await this.saveReport(report);
      this.printSummary(report);
      
    } finally {
      await this.teardown();
    }
  }
  
  /**
   * 运行单个测试场景
   */
  async runScenario(scenario: TestScenario): Promise<void> {
    logger.info(`\nRunning: ${scenario.name}`);
    logger.info(`Description: ${scenario.description}`);
    
    // 预热运行
    if (scenario.warmup !== false) {
      logger.debug('Warming up...');
      await scenario.run();
      this.monitor.clear();
    }
    
    // 正式运行
    const iterations = scenario.iterations || 1;
    for (let i = 0; i < iterations; i++) {
      logger.debug(`Iteration ${i + 1}/${iterations}`);
      await scenario.run();
    }
  }
  
  /**
   * 保存报告
   */
  async saveReport(report: any): Promise<void> {
    const reportPath = path.join(process.cwd(), 'performance-report.json');
    await fs.writeJson(reportPath, report, { spaces: 2 });
    logger.info(`\nPerformance report saved to: ${reportPath}`);
  }
  
  /**
   * 打印摘要
   */
  printSummary(report: any): void {
    console.log('\n' + '='.repeat(60));
    console.log('PERFORMANCE TEST SUMMARY');
    console.log('='.repeat(60));
    
    console.log('\nSystem Info:');
    console.log(`- Platform: ${report.systemInfo.platform}`);
    console.log(`- CPUs: ${report.systemInfo.cpus}`);
    console.log(`- Memory: ${(report.systemInfo.totalMemory / 1024 / 1024 / 1024).toFixed(2)} GB`);
    console.log(`- Node Version: ${report.systemInfo.nodeVersion}`);
    
    console.log('\nPerformance Summary:');
    console.log(`- Total Operations: ${report.summary.totalOperations}`);
    console.log(`- Total Duration: ${report.summary.totalDuration.toFixed(2)}ms`);
    console.log(`- Average Duration: ${report.summary.averageDuration.toFixed(2)}ms`);
    
    if (report.summary.slowestOperation) {
      console.log(`\nSlowest Operation:`);
      console.log(`- ${report.summary.slowestOperation.operation}: ${report.summary.slowestOperation.duration.toFixed(2)}ms`);
    }
    
    if (report.summary.fastestOperation) {
      console.log(`\nFastest Operation:`);
      console.log(`- ${report.summary.fastestOperation.operation}: ${report.summary.fastestOperation.duration.toFixed(2)}ms`);
    }
    
    console.log(`\nResource Usage:`);
    console.log(`- Peak Memory: ${(report.summary.memoryPeakUsage / 1024 / 1024).toFixed(2)} MB`);
    console.log(`- Peak CPU: ${report.summary.cpuPeakUsage.toFixed(2)}%`);
    
    if (report.recommendations.length > 0) {
      console.log('\nRecommendations:');
      report.recommendations.forEach((rec: string) => {
        console.log(`- ${rec}`);
      });
    }
    
    console.log('\n' + '='.repeat(60));
  }
}

/**
 * 运行性能测试
 */
export async function runPerformanceTests(): Promise<void> {
  const suite = new PerformanceTestSuite();
  await suite.runAll();
}